/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Spinner from '@components/spinner';
import { DashboardWidgetRenderer } from '@pages/home/dashboard-widget-renderer';
import { useDashboard, WidgetItem } from '@/contexts/dashboard-context';
import { WidgetType } from '@utils/widget-registry';
import {
    ActivityCounterConfig,
    ActivityCounterModalState,
    ShortcutConfig,
    ShortcutModalState,
    WidgetGridSize,
    WidgetSelectionOption,
} from './home.types';
import { ActivityCounterSetupModal, ShortcutSetupModal, WidgetSelectorModal } from './home-widget-modals';

const GAP = 24;
const PADDING_X = 16;
const PADDING_Y = 16;
const LABEL_HEIGHT = 24;
const MIN_CELL_SIZE = 72;
const MAX_CELL_SIZE = 112;
const MAX_COLS = 24;
const TARGET_CELL_SIZE = 96;

interface PositionedWidget {
    gridX: number;
    gridY: number;
    page: number;
    pageX: number;
    pageY: number;
    displayCols: number;
    displayRows: number;
}

function isUuid(value: string) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function isActivityCounterWidget(widgetType?: WidgetType) {
    return widgetType?.name === 'activity_counter' || widgetType?.componentId === 'ActivityCounterWidget';
}

function getWidgetDimensions(size: WidgetGridSize) {
    const dimensions: Record<WidgetGridSize, [number, number]> = {
        '1x1': [1, 1],
        '1x2': [1, 2],
        '2x2': [2, 2],
        '2x4': [2, 4],
        '3x4': [3, 4],
    };

    return dimensions[size];
}

function normalizeShortcutUrl(url: string) {
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function getShortcutLabel(config?: Record<string, unknown>) {
    const title = typeof config?.title === 'string' ? config.title.trim() : '';
    if (title) return title;

    const url = typeof config?.url === 'string' ? config.url : '';
    if (!url) return 'Shortcut';

    try {
        return new URL(url).hostname;
    } catch {
        return 'Shortcut';
    }
}

function getWidgetLabel(item: WidgetItem) {
    if (item.widgetType?.name === 'shortcut' || item.widgetType?.componentId === 'ShortcutWidget') {
        return getShortcutLabel(item.config);
    }

    return item.widgetType?.displayName ?? item.widgetType?.name ?? 'Widget';
}

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

function ContextMenuItem({
    icon,
    label,
    onClick,
    destructive,
}: {
    icon: string;
    label: string;
    onClick: () => void;
    destructive?: boolean;
}) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={onClick}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium transition-colors ${
                destructive ? 'text-theme-error hover:bg-theme-error-soft' : 'text-theme-text hover:bg-theme-hover'
            }`}
        >
            <span className={`material-symbols-rounded text-lg leading-none ${destructive ? '' : 'text-theme-icon'}`}>
                {icon}
            </span>
            {label}
        </button>
    );
}

/**
 * Home dashboard grid responsible for widget add, edit, drag, and reflow.
 *
 * The layout math lives here so the page shell can stay small and the context
 * remains the single source of truth for persisted widget state.
 */
export function HomeDashboardGrid() {
    const { layout, updateWidget, updateWidgetPosition, saveLayout, deleteWidget, addWidget, availableWidgetTypes } =
        useDashboard();
    const [draggedItem, setDraggedItem] = useState<{ id: string; startX: number; startY: number } | null>(null);
    const [dragPreview, setDragPreview] = useState<{ gridX: number; gridY: number } | null>(null);
    const [localPositions, setLocalPositions] = useState<Record<string, { gridX: number; gridY: number }>>({});
    const [lastMovedId, setLastMovedId] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const [isPendingSave, setIsPendingSave] = useState(false);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastPreviewUpdateRef = useRef(0);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [shortcutModalState, setShortcutModalState] = useState<ShortcutModalState | null>(null);
    const [activityCounterModalState, setActivityCounterModalState] = useState<ActivityCounterModalState | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; widgetId: string | null } | null>(null);
    const [containerSize, setContainerSize] = useState(() => ({
        width: typeof window !== 'undefined' ? window.innerWidth : 1200,
        height: typeof window !== 'undefined' ? window.innerHeight : 800,
    }));

    const resolveWidgetTypeId = (widgetTypeId: string) => {
        const widgetType = availableWidgetTypes.find((type: WidgetType) => type.id === widgetTypeId);
        if (!widgetType) return null;
        if (isUuid(widgetType.id)) return widgetType.id;

        const resolved = availableWidgetTypes.find(
            (type: WidgetType) => type.name === widgetType.name && isUuid(type.id)
        );
        return resolved?.id ?? null;
    };

    const getDefaultWidgetSize = (widgetTypeId: string) => {
        const widgetType = availableWidgetTypes.find((type: WidgetType) => type.id === widgetTypeId);
        if (widgetType?.name === 'shortcut' || widgetType?.componentId === 'ShortcutWidget') {
            return '1x1';
        }

        const preferredSizes: WidgetGridSize[] = ['2x2', '1x2', '1x1', '2x4', '3x4'];
        const supported = (widgetType?.supportedSizes ?? []) as WidgetGridSize[];
        const preferred = preferredSizes.find((size) => supported.includes(size));
        return preferred ?? supported[0] ?? '2x2';
    };

    const handleAddWidget = async (option: WidgetSelectionOption, shortcutConfig?: ShortcutConfig) => {
        const widgetType = availableWidgetTypes.find((type: WidgetType) => type.id === option.widgetTypeId);
        const resolvedWidgetTypeId = resolveWidgetTypeId(option.widgetTypeId);
        if (!resolvedWidgetTypeId) {
            console.warn('Widget type not available on server:', option.widgetTypeId);
            return;
        }

        const widgetSize = option.widgetSize ?? getDefaultWidgetSize(option.widgetTypeId);
        const isShortcut = widgetType?.name === 'shortcut' || widgetType?.componentId === 'ShortcutWidget';
        const config = isShortcut
            ? {
                  url: normalizeShortcutUrl(shortcutConfig?.url ?? ''),
                  title: shortcutConfig?.title ?? '',
              }
            : option.presetConfig;

        await addWidget({
            widgetTypeId: resolvedWidgetTypeId,
            widgetSize,
            gridX: 0,
            gridY: 0,
            config,
            isVisible: true,
            isLocked: false,
            zOrder: 0,
        });
    };

    const handleWidgetSelect = async (option: WidgetSelectionOption) => {
        const widgetType = availableWidgetTypes.find((type: WidgetType) => type.id === option.widgetTypeId);
        const isShortcut = widgetType?.name === 'shortcut' || widgetType?.componentId === 'ShortcutWidget';

        if (isShortcut) {
            setShortcutModalState({ mode: 'add', option, title: '', url: '' });
            return;
        }

        if (isActivityCounterWidget(widgetType)) {
            setActivityCounterModalState({
                mode: 'add',
                option,
                activityId: '',
                variant: option.presetConfig?.variant === 'accent' ? 'accent' : 'plain',
            });
            return;
        }

        await handleAddWidget(option);
    };

    const openShortcutEditModal = (
        widgetId: string,
        option: WidgetSelectionOption,
        config?: Record<string, unknown>
    ) => {
        setShortcutModalState({
            mode: 'edit',
            option,
            widgetId,
            title: typeof config?.title === 'string' ? config.title : '',
            url: typeof config?.url === 'string' ? config.url : '',
        });
    };

    const handleShortcutConfirm = async (payload: ShortcutConfig) => {
        if (!shortcutModalState?.option) return;

        if (shortcutModalState.mode === 'add') {
            await handleAddWidget(shortcutModalState.option, payload);
        } else if (shortcutModalState.widgetId) {
            await updateWidget(shortcutModalState.widgetId, {
                config: {
                    url: normalizeShortcutUrl(payload.url),
                    title: payload.title,
                },
            });
        }

        setShortcutModalState(null);
    };

    const openActivityCounterEditModal = (item: WidgetItem) => {
        setActivityCounterModalState({
            mode: 'edit',
            widgetId: item.id,
            activityId: typeof item.config?.activityId === 'string' ? item.config.activityId : '',
            variant: item.config?.variant === 'accent' ? 'accent' : 'plain',
        });
    };

    const handleActivityCounterConfirm = async (payload: ActivityCounterConfig) => {
        if (!activityCounterModalState) return;

        if (activityCounterModalState.mode === 'add' && activityCounterModalState.option) {
            await handleAddWidget({ ...activityCounterModalState.option, presetConfig: payload });
        } else if (activityCounterModalState.widgetId) {
            await updateWidget(activityCounterModalState.widgetId, { config: payload });
        }

        setActivityCounterModalState(null);
    };

    const calculateResponsiveLayout = (width: number, height: number) => {
        const resolvedWidth = width > 0 ? width : typeof window !== 'undefined' ? window.innerWidth : 1200;
        const resolvedHeight = height > 0 ? height : typeof window !== 'undefined' ? window.innerHeight : 800;
        const availableWidth = Math.max(1, resolvedWidth - PADDING_X * 2);
        const availableHeight = Math.max(1, resolvedHeight - PADDING_Y * 2 - LABEL_HEIGHT);
        let bestCols = 1;
        let bestRows = 1;
        let bestCellSize = Math.max(1, Math.min(MAX_CELL_SIZE, Math.floor(availableWidth)));
        let bestCapacity = 0;
        let bestDiff = Number.POSITIVE_INFINITY;

        for (let cols = 1; cols <= MAX_COLS; cols += 1) {
            const cellSize = Math.floor((availableWidth - GAP * (cols - 1)) / cols);
            if (cellSize <= 0) continue;
            if (cellSize < MIN_CELL_SIZE || cellSize > MAX_CELL_SIZE) continue;
            const rows = Math.max(1, Math.floor((availableHeight + GAP) / (cellSize + GAP)));
            const diff = Math.abs(cellSize - TARGET_CELL_SIZE);
            const capacity = cols * rows;

            if (capacity > bestCapacity || (capacity === bestCapacity && diff < bestDiff)) {
                bestCapacity = capacity;
                bestDiff = diff;
                bestCols = cols;
                bestRows = rows;
                bestCellSize = cellSize;
            }
        }

        if (bestCapacity === 0) {
            bestCols = Math.max(1, Math.min(MAX_COLS, Math.floor((availableWidth + GAP) / (MIN_CELL_SIZE + GAP))));
            bestCellSize = Math.floor((availableWidth - GAP * (bestCols - 1)) / bestCols);
            if (!Number.isFinite(bestCellSize) || bestCellSize <= 0) {
                bestCols = 1;
                bestCellSize = Math.max(1, Math.floor(availableWidth));
            }
            bestCellSize = clamp(bestCellSize, 1, MAX_CELL_SIZE);
            bestRows = Math.max(1, Math.floor((availableHeight + GAP) / (bestCellSize + GAP)));
        }

        const gridWidth = bestCols * bestCellSize + GAP * (bestCols - 1);
        const gridHeight = bestRows * bestCellSize + GAP * (bestRows - 1);
        const gridOffsetX = PADDING_X + Math.max(0, Math.floor((availableWidth - gridWidth) / 2));
        const gridOffsetY = Math.max(PADDING_Y, Math.floor((resolvedHeight - gridHeight - LABEL_HEIGHT) / 2));

        return {
            cols: bestCols,
            rows: bestRows,
            cellSize: bestCellSize,
            gridWidth,
            gridHeight,
            gridOffsetX,
            gridOffsetY,
            pageWidth: resolvedWidth,
            pageHeight: resolvedHeight,
        };
    };

    const getDesiredPosition = (item: WidgetItem) => {
        const override = localPositions[item.id];
        if (override) {
            return override;
        }

        return { gridX: item.gridX ?? 0, gridY: item.gridY ?? 0 };
    };

    const calculateWidgetPositions = (items: WidgetItem[], previewGridX?: number, previewGridY?: number) => {
        const layoutMetrics = calculateResponsiveLayout(containerSize.width, containerSize.height);
        const { cols, rows } = layoutMetrics;
        const positions = new Map<string, PositionedWidget>();
        const occupiedCells = new Set<string>();

        const sortedItems = [...items].sort((a, b) => {
            if (lastMovedId) {
                if (a.id === lastMovedId) return -1;
                if (b.id === lastMovedId) return 1;
            }

            const aPos = getDesiredPosition(a);
            const bPos = getDesiredPosition(b);
            if (aPos.gridY !== bPos.gridY) return aPos.gridY - bPos.gridY;
            return aPos.gridX - bPos.gridX;
        });

        const occupiedArea = sortedItems.reduce((total, item) => {
            const [itemRows, itemCols] = getWidgetDimensions(item.widgetSize);
            return total + Math.min(itemRows, rows) * Math.min(itemCols, cols);
        }, 0);
        const estimatedPages = Math.max(1, Math.ceil(occupiedArea / Math.max(1, cols * rows)));

        sortedItems.forEach((item) => {
            const [itemRows, itemCols] = getWidgetDimensions(item.widgetSize);
            const displayCols = Math.min(itemCols, cols);
            const displayRows = Math.min(itemRows, rows);

            let desired = getDesiredPosition(item);
            if (draggedItem?.id === item.id && previewGridX !== undefined && previewGridY !== undefined) {
                desired = { gridX: previewGridX, gridY: previewGridY };
            }

            const startX = Math.max(0, Math.min(desired.gridX, Math.max(0, cols - displayCols)));
            const startY = Math.max(0, desired.gridY);
            const startPage = Math.floor(startY / rows);
            const startPageY = startY % rows;

            let gridX = startX;
            let page = startPage;
            let pageX = startX;
            let pageY = Math.min(startPageY, Math.max(0, rows - displayRows));
            let found = false;
            const maxSearchPages = startPage + estimatedPages + sortedItems.length + 2;

            for (let pageIndex = startPage; pageIndex < maxSearchPages; pageIndex += 1) {
                const yStart = pageIndex === startPage ? startPageY : 0;
                for (let y = yStart; y <= rows - displayRows; y += 1) {
                    const xStart = pageIndex === startPage && y === yStart ? startX : 0;
                    for (let x = xStart; x <= cols - displayCols; x += 1) {
                        let canPlace = true;
                        for (let dy = 0; dy < displayRows; dy += 1) {
                            for (let dx = 0; dx < displayCols; dx += 1) {
                                const cellKey = `${pageIndex},${x + dx},${y + dy}`;
                                if (occupiedCells.has(cellKey)) {
                                    canPlace = false;
                                    break;
                                }
                            }
                            if (!canPlace) break;
                        }

                        if (canPlace) {
                            gridX = x;
                            page = pageIndex;
                            pageX = x;
                            pageY = y;
                            for (let dy = 0; dy < displayRows; dy += 1) {
                                for (let dx = 0; dx < displayCols; dx += 1) {
                                    occupiedCells.add(`${pageIndex},${x + dx},${y + dy}`);
                                }
                            }
                            found = true;
                            break;
                        }
                    }
                    if (found) break;
                }
                if (found) break;
            }

            positions.set(item.id, {
                gridX,
                gridY: page * rows + pageY,
                page,
                pageX,
                pageY,
                displayCols,
                displayRows,
            });
        });

        return { positions, ...layoutMetrics };
    };

    useLayoutEffect(() => {
        const measureContainer = () => {
            const rect = containerRef.current?.getBoundingClientRect();
            const viewportWidth = window.innerWidth ?? rect?.width ?? 0;
            const viewportHeight = window.innerHeight ?? rect?.height ?? 0;
            const width = rect && rect.width > 0 ? rect.width : viewportWidth;
            const height = rect && rect.height > 0 ? rect.height : viewportHeight;
            const safeWidth = width > 0 ? width : 1200;
            const safeHeight = height > 0 ? height : 800;
            setContainerSize({ width: safeWidth, height: safeHeight });
        };

        measureContainer();
        const rafId = requestAnimationFrame(measureContainer);

        const handleResize = () => {
            measureContainer();
        };

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
            }
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            observer.disconnect();
            cancelAnimationFrame(rafId);
        };
    }, []);

    useEffect(() => {
        if (!layout) return;

        const rafId = requestAnimationFrame(() => {
            const rect = containerRef.current?.getBoundingClientRect();
            const viewportWidth = window.innerWidth ?? rect?.width ?? 0;
            const viewportHeight = window.innerHeight ?? rect?.height ?? 0;
            const width = rect && rect.width > 0 ? rect.width : viewportWidth;
            const height = rect && rect.height > 0 ? rect.height : viewportHeight;
            const safeWidth = width > 0 ? width : 1200;
            const safeHeight = height > 0 ? height : 800;
            setContainerSize({ width: safeWidth, height: safeHeight });
        });

        return () => cancelAnimationFrame(rafId);
    }, [layout]);

    const handleDragStart = (event: React.DragEvent, itemId: string) => {
        const target = event.target as HTMLElement;
        if (target.closest('button') || !isEditMode) {
            event.preventDefault();
            return;
        }

        setDraggedItem({ id: itemId, startX: event.clientX, startY: event.clientY });
        event.dataTransfer.effectAllowed = 'move';
    };

    const abortDrag = useCallback(() => {
        setDraggedItem(null);
        setDragPreview(null);
        if (dragThrottleRef.current) {
            clearTimeout(dragThrottleRef.current);
            dragThrottleRef.current = null;
        }
        lastPreviewUpdateRef.current = 0;
    }, []);

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        if (!draggedItem || !gridRef.current) return;

        const gridRect = gridRef.current.getBoundingClientRect();
        const relX = event.clientX - gridRect.left;
        const relY = event.clientY - gridRect.top;

        const applyPreviewUpdate = () => {
            if (!gridRef.current) return;

            const { cellSize, cols, rows, gridOffsetX, gridOffsetY } = calculateResponsiveLayout(
                containerSize.width,
                containerSize.height
            );
            const pageWidth = Math.max(1, gridRect.width);
            const scrollX = gridRef.current.scrollLeft + relX;
            const page = Math.max(0, Math.floor(scrollX / pageWidth));
            const pageLocalX = scrollX - page * pageWidth;
            const rawGridX = Math.floor((pageLocalX - gridOffsetX) / (cellSize + GAP));
            const rawGridY = Math.floor((relY - gridOffsetY) / (cellSize + GAP));
            const draggedMeta = layout?.items.find((item) => item.id === draggedItem.id);
            const [dragRows, dragCols] = draggedMeta ? getWidgetDimensions(draggedMeta.widgetSize) : [1, 1];
            const displayCols = Math.min(dragCols, cols);
            const displayRows = Math.min(dragRows, rows);
            const maxGridX = Math.max(0, cols - displayCols);
            const maxGridY = Math.max(0, rows - displayRows);
            const newGridX = clamp(rawGridX, 0, maxGridX);
            const newGridY = page * rows + clamp(rawGridY, 0, maxGridY);

            const edgeSize = 56;
            const maxScrollLeft = gridRef.current.scrollWidth - gridRef.current.clientWidth;
            if (relX > gridRect.width - edgeSize && gridRef.current.scrollLeft < maxScrollLeft - 1) {
                gridRef.current.scrollBy({ left: pageWidth, behavior: 'smooth' });
            } else if (relX < edgeSize && gridRef.current.scrollLeft > 0) {
                gridRef.current.scrollBy({ left: -pageWidth, behavior: 'smooth' });
            }

            setDragPreview({ gridX: newGridX, gridY: newGridY });
            lastPreviewUpdateRef.current = Date.now();
        };

        const now = Date.now();
        const elapsed = now - lastPreviewUpdateRef.current;
        const throttleMs = 50;

        if (elapsed >= throttleMs) {
            if (dragThrottleRef.current) {
                clearTimeout(dragThrottleRef.current);
                dragThrottleRef.current = null;
            }
            applyPreviewUpdate();
            return;
        }

        if (dragThrottleRef.current) {
            clearTimeout(dragThrottleRef.current);
        }

        dragThrottleRef.current = setTimeout(() => {
            applyPreviewUpdate();
            dragThrottleRef.current = null;
        }, throttleMs - elapsed);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        if (!draggedItem) return;
        const leftViewport =
            event.clientX <= 0 ||
            event.clientY <= 0 ||
            event.clientX >= window.innerWidth ||
            event.clientY >= window.innerHeight;
        if (leftViewport) {
            abortDrag();
        }
    };

    useEffect(() => {
        if (!draggedItem) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                abortDrag();
            }
        };

        const onWindowBlur = () => {
            abortDrag();
        };

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('blur', onWindowBlur);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('blur', onWindowBlur);
        };
    }, [abortDrag, draggedItem]);

    useEffect(() => {
        if (!contextMenu) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setContextMenu(null);
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', () => setContextMenu(null), { once: true });
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [contextMenu]);

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        const target = event.target as HTMLElement;
        const widgetEl = target.closest('[data-widget-id]');
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            widgetId: widgetEl?.getAttribute('data-widget-id') ?? null,
        });
    };

    const handleDrop = async (event: React.DragEvent) => {
        event.preventDefault();
        if (!layout || !draggedItem || !gridRef.current) return;

        const dropPreview = dragPreview;
        if (!dropPreview) {
            abortDrag();
            return;
        }

        const visibleItems = layout.items.filter((item) => item.isVisible);
        const { positions: resolvedPositions } = calculateWidgetPositions(
            visibleItems,
            dropPreview.gridX,
            dropPreview.gridY
        );

        const committedPositions = Object.fromEntries(
            Array.from(resolvedPositions.entries()).map(([id, pos]) => [id, { gridX: pos.gridX, gridY: pos.gridY }])
        );
        setLocalPositions((prev) => ({ ...prev, ...committedPositions }));
        setLastMovedId(draggedItem.id);

        const pendingUpdates = visibleItems.filter((item) => {
            const resolved = resolvedPositions.get(item.id);
            return resolved && (resolved.gridX !== item.gridX || resolved.gridY !== item.gridY);
        });

        await Promise.all(
            pendingUpdates.map(async (item) => {
                const resolved = resolvedPositions.get(item.id);
                if (!resolved) return;
                await updateWidgetPosition(item.id, resolved.gridX, resolved.gridY);
            })
        );

        setIsPendingSave(true);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            void (async () => {
                await saveLayout();
                setIsPendingSave(false);
            })();
        }, 500);

        abortDrag();
    };

    const handlePageScroll = () => {
        if (!gridRef.current) return;
        const pageWidth = Math.max(1, gridRef.current.clientWidth);
        setCurrentPage(Math.round(gridRef.current.scrollLeft / pageWidth));
    };

    const scrollToPage = (page: number) => {
        if (!gridRef.current) return;
        const pageWidth = Math.max(1, gridRef.current.clientWidth);
        gridRef.current.scrollTo({ left: page * pageWidth, behavior: 'smooth' });
        setCurrentPage(page);
    };

    if (!layout) return <Spinner />;

    const sortedItems = [...layout.items].filter((item) => item.isVisible);
    const baseLayout = calculateWidgetPositions(sortedItems);
    const previewLayout =
        draggedItem && dragPreview ? calculateWidgetPositions(sortedItems, dragPreview.gridX, dragPreview.gridY) : null;
    const positions = previewLayout?.positions ?? baseLayout.positions;
    const cols = previewLayout?.cols ?? baseLayout.cols;
    const rows = previewLayout?.rows ?? baseLayout.rows;
    const cellSize = previewLayout?.cellSize ?? baseLayout.cellSize;
    const gridWidth = previewLayout?.gridWidth ?? baseLayout.gridWidth;
    const gridHeight = previewLayout?.gridHeight ?? baseLayout.gridHeight;
    const gridOffsetX = previewLayout?.gridOffsetX ?? baseLayout.gridOffsetX;
    const gridOffsetY = previewLayout?.gridOffsetY ?? baseLayout.gridOffsetY;

    const shiftedWidgetCount = previewLayout
        ? sortedItems.filter((item) => {
              const before = baseLayout.positions.get(item.id);
              const after = previewLayout.positions.get(item.id);
              if (!before || !after || item.id === draggedItem?.id) return false;
              return before.gridX !== after.gridX || before.gridY !== after.gridY;
          }).length
        : 0;

    const occupiedPageCount = Math.max(
        ...(sortedItems.length > 0
            ? sortedItems.map((item) => {
                  const pos = positions.get(item.id);
                  return pos ? pos.page + 1 : 1;
              })
            : [1])
    );

    const pageCount = Math.max(1, occupiedPageCount + (isEditMode || draggedItem ? 1 : 0));
    const visibleCurrentPage = clamp(currentPage, 0, pageCount - 1);
    const pageIndexes = Array.from({ length: pageCount }, (_, pageIndex) => pageIndex);

    return (
        <>
            <WidgetSelectorModal
                isOpen={isSelectorOpen}
                onClose={() => setIsSelectorOpen(false)}
                onSelect={handleWidgetSelect}
                availableWidgetTypes={availableWidgetTypes}
            />

            <ShortcutSetupModal
                isOpen={Boolean(shortcutModalState)}
                onClose={() => setShortcutModalState(null)}
                onConfirm={handleShortcutConfirm}
                heading={shortcutModalState?.mode === 'edit' ? 'Configure Shortcut' : 'Add Shortcut'}
                initialTitle={shortcutModalState?.title ?? ''}
                initialUrl={shortcutModalState?.url ?? ''}
                confirmLabel={shortcutModalState?.mode === 'edit' ? 'Save Changes' : 'Add Shortcut'}
                description={
                    shortcutModalState?.mode === 'edit'
                        ? 'Update the shortcut destination and label in a dedicated panel.'
                        : 'Create a new quick launch shortcut for your dashboard.'
                }
            />

            <ActivityCounterSetupModal
                isOpen={Boolean(activityCounterModalState)}
                onClose={() => setActivityCounterModalState(null)}
                onConfirm={handleActivityCounterConfirm}
                heading={
                    activityCounterModalState?.mode === 'edit' ? 'Configure Activity Counter' : 'Add Activity Counter'
                }
                confirmLabel={activityCounterModalState?.mode === 'edit' ? 'Save Changes' : 'Add Counter'}
                initialActivityId={activityCounterModalState?.activityId ?? ''}
                initialVariant={activityCounterModalState?.variant ?? 'plain'}
            />

            <div className="flex h-full min-h-0 w-full flex-col overflow-hidden py-4">
                <style>{`@keyframes hf-jiggle { 0% { transform: rotate(-1.35deg); } 100% { transform: rotate(1.35deg); } }`}</style>

                <div className="shrink-0 px-4">
                    {isEditMode && (
                        <div className="mb-4 flex justify-center">
                            <div className="border-theme-border/60 bg-theme-popup-bg/85 flex items-center gap-1.5 rounded-full border p-1.5 shadow-lg backdrop-blur-xl">
                                <button
                                    onClick={() => setIsSelectorOpen(true)}
                                    className="text-theme-text hover:bg-theme-hover inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition active:scale-95"
                                >
                                    <span className="material-symbols-rounded text-lg leading-none">add</span>
                                    Add Widget
                                </button>
                                <button
                                    onClick={() => setIsEditMode(false)}
                                    className="bg-theme-active text-theme-active-contrast hover:bg-theme-button-hover inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition active:scale-95"
                                >
                                    <span className="material-symbols-rounded text-lg leading-none">done</span>
                                    Done
                                </button>
                            </div>
                        </div>
                    )}

                    {isPendingSave && (
                        <div className="bg-theme-warning-soft text-theme-warning mb-4 flex items-center gap-2 rounded-2xl p-3 text-sm shadow-sm">
                            <span className="material-symbols-rounded animate-spin">hourglass</span>
                            Saving layout...
                        </div>
                    )}

                    {draggedItem && dragPreview && (
                        <div className="border-theme-badge-blue/60 bg-theme-bg-blue-light text-theme-text mb-4 flex items-center justify-between gap-3 rounded-2xl border p-3 text-sm shadow-sm">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-rounded">drag_indicator</span>
                                <span>
                                    Page {Math.floor(dragPreview.gridY / rows) + 1}, row{' '}
                                    {(dragPreview.gridY % rows) + 1}, column {dragPreview.gridX + 1}
                                    {shiftedWidgetCount > 0
                                        ? ` - ${shiftedWidgetCount} other widget${shiftedWidgetCount > 1 ? 's' : ''} will shift`
                                        : ''}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={abortDrag}
                                className="border-theme-border bg-theme-content-bg hover:bg-theme-hover text-theme-text rounded-full border px-3 py-1 text-xs font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

                <div
                    ref={containerRef}
                    onContextMenu={handleContextMenu}
                    className="relative min-h-0 w-full flex-1 overflow-hidden"
                >
                    {sortedItems.length === 0 ? (
                        <div className="flex h-full w-full items-center justify-center px-4">
                            <div className="border-theme-border/70 bg-theme-card-bg/60 text-theme-text w-full max-w-md rounded-[1.75rem] border-2 border-dashed p-8 text-center backdrop-blur-xl">
                                <span className="material-symbols-rounded text-theme-icon mx-auto mb-4 block text-5xl">
                                    dashboard
                                </span>
                                <h3 className="mb-2 text-lg font-semibold">Your Dashboard is Empty</h3>
                                <p className="text-theme-text-light mb-6 text-sm">
                                    Add widgets to build your dashboard.
                                </p>
                                <button
                                    onClick={() => setIsSelectorOpen(true)}
                                    className="bg-theme-active text-theme-active-contrast hover:bg-theme-button-hover inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium shadow-sm transition active:scale-95"
                                >
                                    <span className="material-symbols-rounded text-lg">add</span>
                                    Add Your First Widget
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                ref={gridRef}
                                className="h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth [&::-webkit-scrollbar]:hidden"
                                style={{ scrollbarWidth: 'none' }}
                                onScroll={handlePageScroll}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={(event) => {
                                    void handleDrop(event);
                                }}
                            >
                                <div className="flex h-full">
                                    {pageIndexes.map((pageIndex) => (
                                        <div
                                            key={pageIndex}
                                            className="relative h-full shrink-0 grow-0 basis-full snap-start overflow-hidden"
                                        >
                                            <div
                                                className="absolute overflow-visible"
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
                                                    gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
                                                    gridAutoRows: `${cellSize}px`,
                                                    gap: `${GAP}px`,
                                                    width: `${gridWidth}px`,
                                                    height: `${gridHeight}px`,
                                                    left: `${gridOffsetX}px`,
                                                    top: `${gridOffsetY}px`,
                                                }}
                                            >
                                                {sortedItems.map((item) => {
                                                    const pos = positions.get(item.id);
                                                    if (pos?.page !== pageIndex) return null;

                                                    const label = getWidgetLabel(item);
                                                    const widgetIndex = sortedItems.findIndex(
                                                        (candidate) => candidate.id === item.id
                                                    );
                                                    const jiggleDelay = `${(widgetIndex % 5) * 0.04}s`;

                                                    return (
                                                        <div
                                                            key={item.id}
                                                            data-widget-id={item.id}
                                                            draggable={isEditMode}
                                                            onDragStart={(event) => handleDragStart(event, item.id)}
                                                            onDragEnd={abortDrag}
                                                            className={`group relative overflow-visible ${
                                                                isEditMode ? 'cursor-grab' : ''
                                                            } ${draggedItem?.id === item.id ? 'opacity-50' : ''}`}
                                                            style={{
                                                                gridColumn: `${pos.pageX + 1} / span ${pos.displayCols}`,
                                                                gridRow: `${pos.pageY + 1} / span ${pos.displayRows}`,
                                                                animation: isEditMode
                                                                    ? `hf-jiggle 0.22s ease-in-out ${jiggleDelay} infinite alternate`
                                                                    : undefined,
                                                            }}
                                                        >
                                                            <div
                                                                className={`border-theme-border/50 bg-theme-card-bg relative h-full overflow-hidden rounded-[22px] border shadow-[0_10px_30px_-14px_rgba(2,6,23,0.30)] transition-all duration-200 ${
                                                                    draggedItem?.id === item.id
                                                                        ? 'ring-theme-link/80 ring-2'
                                                                        : previewLayout &&
                                                                            baseLayout.positions.get(item.id) &&
                                                                            previewLayout.positions.get(item.id) &&
                                                                            (baseLayout.positions.get(item.id)!
                                                                                .gridX !==
                                                                                previewLayout.positions.get(item.id)!
                                                                                    .gridX ||
                                                                                baseLayout.positions.get(item.id)!
                                                                                    .gridY !==
                                                                                    previewLayout.positions.get(
                                                                                        item.id
                                                                                    )!.gridY)
                                                                          ? 'ring-theme-badge-yellow/80 ring-2'
                                                                          : isEditMode
                                                                            ? 'hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(2,6,23,0.40)] active:cursor-grabbing'
                                                                            : ''
                                                                }`}
                                                            >
                                                                <div className="flex h-full flex-col">
                                                                    <div className="flex-1 overflow-auto">
                                                                        <DashboardWidgetRenderer
                                                                            item={item}
                                                                            isEditMode={isEditMode}
                                                                            isFrozen={isEditMode}
                                                                            onRequestConfigure={
                                                                                item.widgetType?.name === 'shortcut' ||
                                                                                item.widgetType?.componentId ===
                                                                                    'ShortcutWidget'
                                                                                    ? () =>
                                                                                          openShortcutEditModal(
                                                                                              item.id,
                                                                                              {
                                                                                                  widgetTypeId:
                                                                                                      item.widgetTypeId,
                                                                                                  title:
                                                                                                      item.widgetType
                                                                                                          ?.displayName ??
                                                                                                      'Shortcut',
                                                                                                  subtitle:
                                                                                                      item.widgetType
                                                                                                          ?.name ??
                                                                                                      'shortcut',
                                                                                              },
                                                                                              item.config
                                                                                          )
                                                                                    : isActivityCounterWidget(
                                                                                            item.widgetType
                                                                                        )
                                                                                      ? () =>
                                                                                            openActivityCounterEditModal(
                                                                                                item
                                                                                            )
                                                                                      : undefined
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {isEditMode && (
                                                                <button
                                                                    onClick={() => deleteWidget(item.id)}
                                                                    className="border-theme-border bg-theme-card-bg text-theme-text absolute -top-1.5 -left-1.5 z-20 flex h-6 w-6 items-center justify-center rounded-full border shadow-md transition hover:scale-105 active:scale-95"
                                                                    title="Remove widget"
                                                                    aria-label="Remove widget"
                                                                >
                                                                    <span className="material-symbols-rounded text-sm leading-none">
                                                                        remove
                                                                    </span>
                                                                </button>
                                                            )}
                                                            <div
                                                                className="text-theme-text-light pointer-events-none absolute left-1/2 -translate-x-1/2 overflow-hidden text-center text-xs font-medium text-ellipsis whitespace-nowrap"
                                                                style={{
                                                                    bottom: `-${LABEL_HEIGHT - 4}px`,
                                                                    width: `${
                                                                        cellSize * pos.displayCols +
                                                                        GAP * (pos.displayCols - 1)
                                                                    }px`,
                                                                }}
                                                            >
                                                                {label}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {pageCount > 1 && (
                                <div className="pointer-events-none absolute right-0 bottom-2 left-0 flex justify-center gap-2">
                                    {pageIndexes.map((pageIndex) => (
                                        <button
                                            key={pageIndex}
                                            type="button"
                                            aria-label={`Go to dashboard page ${pageIndex + 1}`}
                                            onClick={() => scrollToPage(pageIndex)}
                                            className={`pointer-events-auto h-2.5 rounded-full transition-all ${
                                                pageIndex === visibleCurrentPage
                                                    ? 'bg-theme-active w-6'
                                                    : 'bg-theme-text-muted/40 hover:bg-theme-text-muted/70 w-2.5'
                                            }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {contextMenu &&
                        (() => {
                            const ctxItem = contextMenu.widgetId
                                ? sortedItems.find((candidate) => candidate.id === contextMenu.widgetId)
                                : undefined;
                            const ctxIsShortcut =
                                !!ctxItem &&
                                (ctxItem.widgetType?.name === 'shortcut' ||
                                    ctxItem.widgetType?.componentId === 'ShortcutWidget');
                            const ctxIsActivityCounter = !!ctxItem && isActivityCounterWidget(ctxItem.widgetType);
                            const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
                            const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
                            const left = Math.max(8, Math.min(contextMenu.x, viewportW - 220));
                            const top = Math.max(8, Math.min(contextMenu.y, viewportH - 280));
                            return (
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setContextMenu(null)}
                                    onContextMenu={(event) => {
                                        event.preventDefault();
                                        setContextMenu(null);
                                    }}
                                >
                                    <div
                                        role="menu"
                                        onClick={(event) => event.stopPropagation()}
                                        style={{ left, top }}
                                        className="border-theme-border/60 bg-theme-popup-bg/90 text-theme-text absolute min-w-[208px] overflow-hidden rounded-2xl border p-1.5 shadow-xl backdrop-blur-xl"
                                    >
                                        {ctxIsShortcut && ctxItem && (
                                            <ContextMenuItem
                                                icon="edit"
                                                label="Edit Widget"
                                                onClick={() => {
                                                    openShortcutEditModal(
                                                        ctxItem.id,
                                                        {
                                                            widgetTypeId: ctxItem.widgetTypeId,
                                                            title: ctxItem.widgetType?.displayName ?? 'Shortcut',
                                                            subtitle: ctxItem.widgetType?.name ?? 'shortcut',
                                                        },
                                                        ctxItem.config
                                                    );
                                                    setContextMenu(null);
                                                }}
                                            />
                                        )}
                                        {ctxIsActivityCounter && ctxItem && (
                                            <ContextMenuItem
                                                icon="edit"
                                                label="Configure Counter"
                                                onClick={() => {
                                                    openActivityCounterEditModal(ctxItem);
                                                    setContextMenu(null);
                                                }}
                                            />
                                        )}
                                        <ContextMenuItem
                                            icon="add"
                                            label="Add Widget"
                                            onClick={() => {
                                                setIsSelectorOpen(true);
                                                setContextMenu(null);
                                            }}
                                        />
                                        <ContextMenuItem
                                            icon={isEditMode ? 'done' : 'drag_indicator'}
                                            label={isEditMode ? 'Finish Reordering' : 'Reorder Widgets'}
                                            onClick={() => {
                                                setIsEditMode(!isEditMode);
                                                setContextMenu(null);
                                            }}
                                        />
                                        {ctxItem && (
                                            <>
                                                <div className="bg-theme-border-divider/70 my-1 h-px" />
                                                <ContextMenuItem
                                                    icon="delete"
                                                    label="Remove Widget"
                                                    destructive
                                                    onClick={() => {
                                                        void deleteWidget(ctxItem.id);
                                                        setContextMenu(null);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                </div>
            </div>
        </>
    );
}
